import React, { useState } from "react";
import { X, AlertTriangle } from "lucide-react";
import { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ConfirmModalProps {
  isOpen: boolean;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;

  /** NEW INPUT OPTIONS */
  showInput?: boolean;
  inputPlaceholder?: string;
  inputLabel?: string;
  requiredInput?: boolean;

  /** NEW */
  icon?: LucideIcon; // Pass icon component
  iconColor?: string; // Tailwind class e.g. "text-red-600"
  iconBg?: string; // Tailwind class e.g. "bg-red-100"

  onConfirm: (inputValue?: string) => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title = "Are you sure?",
  message = "Please confirm your action.",
  confirmText = "Confirm",
  cancelText = "Cancel",

  icon: Icon = AlertTriangle, // default icon
  iconColor = "text-red-600",
  iconBg = "bg-red-100",

  showInput = false,
  inputPlaceholder = "Enter text...",
  inputLabel,
  requiredInput = false,

  onConfirm,
  onCancel,
}) => {
  const [inputValue, setInputValue] = useState("");
  const { t, i18n } = useTranslation();
  if (!isOpen) return null;

  const handleConfirm = () => {
    if (inputValue) {
      onConfirm(inputValue);
    } else {
      onConfirm();
    }
    setInputValue("");
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div
        className="bg-white rounded-xl w-full max-w-md shadow-lg animate-[slideDown_0.25s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${iconBg}`}>
              <Icon className={`w-5 h-5 ${iconColor}`} />
            </div>
            <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
          </div>

          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* BODY */}
        <div className="px-5 py-4 text-gray-700">{message}</div>

        {showInput && requiredInput && (
          <div className="px-5 text-gray-700">
            {inputLabel && (
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {inputLabel}
              </label>
            )}
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={inputPlaceholder}
              className="w-full border-2 px-5 border-gray-300 rounded-lg py-2 focus:outline-none focus:border-black"
              dir={i18n.language === "ar" ? "rtl" : "ltr"}
            />
          </div>
        )}

        {/* FOOTER */}
        <div className="flex gap-3 px-5 py-4">
          <button
            onClick={onCancel}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 rounded-lg font-medium"
          >
            {cancelText}
          </button>

          <button
            disabled={requiredInput && showInput && !inputValue}
            onClick={handleConfirm}
            className={`
              flex-1 py-2 rounded-lg font-medium transition-all
              ${
                requiredInput && showInput && !inputValue
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : confirmText === "Delete" || confirmText === "Remove"
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-black hover:bg-gray-700 text-white"
              }
            `}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
